import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class PersistentStorageService {
  getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }

    return null;
  }

  /**
   * Retrieves a cookie value and parses it as JSON.
   * Returns the parsed JSON object if the cookie exists, or null if it doesn't.
   */
  getCookieJSON(name: string) {
    const cookieValue = this.getCookie(name);
    return cookieValue !== null ? JSON.parse(cookieValue) : null;
  }

  setCookie(name: string, value: string, days?: number): void {
    let expires = '';

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${encodeURIComponent(value)}${expires}`;
  }

  /**
   * Updates a specific property within a JSON cookie:
   * Retrieves the existing cookie, parses it as JSON, updates the specified property,
   * and saves the modified JSON back to the cookie.
   * delete cookie if empty json object
   * @Param propertyValue of undefined would delete property from the JSON
   */
  setCookieJSON(name: string, propertyName: string, propertyValue: any, days?: number) {
    let cookieJSONString = this.getCookie(name) || '{}';
    cookieJSONString = JSON.stringify({
      ...JSON.parse(cookieJSONString),
      [propertyName]: propertyValue,
    });
    if (cookieJSONString === '{}') {
      this.deleteCookie(name);
    }
    else {
      this.setCookie(name, cookieJSONString, days);
    }
  }

  deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
  }

  existsCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  getAllCookies(): { [key: string]: string } {
    const cookies: { [key: string]: string } = {};
    const cookieArray = document.cookie.split(';');

    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      const [name, value] = cookie.split('=');
      if (name && value) {
        cookies[name] = decodeURIComponent(value);
      }
    }

    return cookies;
  }

  getLocalStorage(name: string): string | null {
    return localStorage.getItem(name);
  }

  /**
   * Retrieves a local storage value and parses it as JSON.
   * Returns the parsed JSON object if the item exists, or null if it doesn't.
   */
  getLocalStorageJSON(name: string) {
    const storageValue = this.getLocalStorage(name);
    return storageValue !== null ? JSON.parse(storageValue) : null;
  }

  setLocalStorage(name: string, value: string): void {
    localStorage.setItem(name, value);
  }

  /**
   * Updates a specific property within a JSON local storage item.
   * Retrieves the existing item, parses it as JSON, updates the specified property,
   * and saves the modified JSON back to local storage.
   * @Param propertyValue of undefined would delete property from the JSON
   */
  setLocalStorageJSON(name: string, propertyName: string, propertyValue: any) {
    let storageJSONString = this.getLocalStorage(name) || '{}';
    storageJSONString = JSON.stringify({
      ...JSON.parse(storageJSONString),
      [propertyName]: propertyValue,
    });
    this.setLocalStorage(name, storageJSONString);
  }

  deleteLocalStorage(name: string): void {
    localStorage.removeItem(name);
  }

  existsLocalStorage(name: string): boolean {
    return this.getLocalStorage(name) !== null;
  }

  getAllLocalStorage(): { [key: string]: string } {
    const storage: { [key: string]: string } = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          storage[key] = value;
        }
      }
    }

    return storage;
  }

  /**
   * Sets a local storage item with an expiration date.
   * The value is stored with metadata including expiry timestamp.
   * Use getLocalStorageWithExpiry to retrieve and automatically check expiration.
   */
  setLocalStorageWithExpiry(name: string, value: string, days: number): void {
    const now = new Date();
    const expiryTime = now.getTime() + days * 24 * 60 * 60 * 1000;

    const item = {
      value: value,
      expiry: expiryTime,
    };

    localStorage.setItem(name, JSON.stringify(item));
  }

  /**
   * Retrieves a local storage item with expiry check.
   * Returns the value if it exists and hasn't expired, or null if expired or doesn't exist.
   * Automatically removes expired items from storage.
   */
  getLocalStorageWithExpiry(name: string): string | null {
    const itemStr = localStorage.getItem(name);

    if (!itemStr) {
      return null;
    }

    try {
      const item = JSON.parse(itemStr);

      if (!item.expiry) {
        return null;
      }

      const now = new Date();

      if (now.getTime() > item.expiry) {
        localStorage.removeItem(name);
        return null;
      }

      return item.value;
    } catch (e) {
      return null;
    }
  }

  /**
   * Sets a JSON object in local storage with an expiration date.
   * The entire object is stored with metadata including expiry timestamp.
   */
  setLocalStorageJSONWithExpiry(name: string, value: any, days: number): void {
    const now = new Date();
    const expiryTime = now.getTime() + days * 24 * 60 * 60 * 1000;

    const item = {
      value: value,
      expiry: expiryTime,
    };

    localStorage.setItem(name, JSON.stringify(item));
  }

  /**
   * Retrieves a JSON object from local storage with expiry check.
   * Returns the parsed JSON object if it exists and hasn't expired, or null if expired or doesn't exist.
   * Automatically removes expired items from storage.
   */
  getLocalStorageJSONWithExpiry(name: string): any | null {
    const itemStr = localStorage.getItem(name);

    if (!itemStr) {
      return null;
    }

    try {
      const item = JSON.parse(itemStr);

      if (!item.expiry) {
        return null;
      }

      const now = new Date();

      if (now.getTime() > item.expiry) {
        localStorage.removeItem(name);
        return null;
      }

      return item.value;
    } catch (e) {
      return null;
    }
  }
}
